import type { LocalDate, LocalDateTime } from "@js-joda/core";
import api from "@/app/api";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type { BmTask } from "@/app/services/generated/bmtaskService";
import {
  bmTaskService,
  BmTaskFilter,
} from "@/app/services/generated/bmtaskService";
import { User } from "@/app/services/generated/userService";
import {
  withLoading,
  wrapPromiseWithErrorHandling,
  makeRef,
  makeDeepRef,
  wrapLoading,
} from "@/components/composables/componentTools";
import { EnumTaskStatus } from "@/app/services/generated/enums/enumTaskStatus.generated";
import type { Ref } from "vue";
import { computed, ref, watch } from "vue";
import { GuidMap, GuidSet } from "@/app/types/common/guidcollections";
import Guid from "@/app/types/common/guid";
import { appendOrdinalSuffix, useI18n } from "@/plugins/i18n";
import type { TaskWithAssociations } from "@/components/pages/tasks/composables/taskAssociations";
import { joinTasksWithAssociations } from "@/components/pages/tasks/composables/taskAssociations";
import tasksStore from "@/store/modules/tasksStore";
import { userService } from "@/app/services/extensions/userServiceExtended";
import { useArrayWithPlaceholderItem } from "@/components/composables/dataUtils";
import type { EnumTaskRepeatBasis } from "@/app/services/generated/enums/enumTaskRepeatBasis.generated";
import apiCache from "@/app/caching/apiCache";
import TaskTypesCache from "@/app/caching/caches/taskTypesCache";
import TaskQueuesCache from "@/app/caching/caches/taskQueuesCache";
import SalesCodesCache from "@/app/caching/caches/salesCodeCache";
import SalesAreasCache from "@/app/caching/caches/salesAreasCache";
import DeliveryAreasCache from "@/app/caching/caches/deliveryAreasCache";
import { deliveryAreaService } from "@/app/services/generated/deliveryareaService";
import { EnumDayOfWeek } from "@/app/services/generated/enums/enumDayOfWeek.generated";
import { distinctGuids, toGuidMap } from "@/app/apiutils";
import type { Contact } from "@/app/services/generated/outletService";
import { outletService } from "@/app/services/generated/outletService";
import Key from "@/app/types/common/key";
import type { ActivityLogEntry } from "@/app/services/generated/activitylogService";
import { activityLogService } from "@/app/services/generated/activitylogService";
import { EnumBmEntityType } from "@/app/services/generated/enums/enumBmEntityType.generated";
import { EnumActivityLogType } from "@/app/services/generated/enums/enumActivityLogType.generated";
import GroupsCache from "@/app/caching/caches/groupsCache";

const PLACEHOLDER_USER_ID = Guid.createEmpty();

/**
 * Determines if the supplied task is overdue in respect to the supplied datetime.
 *
 * `now` would most likely be: LocalDateTime.now(tenantService.getTimezone());
 */
export function isTaskOverdue(task: BmTask, now: LocalDateTime) {
  return (
    !!task.dueDate &&
    (task.dueDate.isBefore(now.toLocalDate()) ||
      (!!task.dueTime &&
        task.dueDate.equals(now.toLocalDate()) &&
        task.dueTime.isBefore(now.toLocalTime())))
  );
}

/**
 * Determines if the task is required to be done on the same day
 * that `now` references
 *
 * We expect not overdue tasks, with no due date, to be in the todo section
 */
export function isTaskInToDoSection(task: BmTask, now: LocalDateTime): boolean {
  if (task.dueDate) {
    if (task.dueTime) {
      return (
        task.dueDate.isEqual(now.toLocalDate()) &&
        task.dueTime.isAfter(now.toLocalTime())
      );
    }

    return task.dueDate.isEqual(now.toLocalDate());
  }

  return true;
}

export function isTaskSelected(
  task: BmTask,
  taskTypeIds: Guid[],
  taskQueueIds: Guid[],
  userOptions: Guid[]
) {
  const taskTypeIsSelected = !!taskTypeIds?.find((typeId) =>
    typeId.equals(task.taskTypeId)
  );
  const taskQueueIsSelected = !!taskQueueIds?.find((queueId) =>
    queueId.equals(task.taskQueueId)
  );
  const taskAssignedUserIsSelected = !!userOptions.find((userId) => {
    // If our selected userId is unassigned, check our task's is also
    if (userId.equals(PLACEHOLDER_USER_ID)) {
      return task.assignedToUserId === null;
    }

    return task.assignedToUserId?.equals(userId);
  });

  return (
    taskTypeIsSelected && taskQueueIsSelected && taskAssignedUserIsSelected
  );
}

export function getRepeatBasisText(task: BmTask, basis?: EnumTaskRepeatBasis) {
  const { $t } = useI18n();

  basis = basis ?? task.taskRepeatBasis;
  const dayOfWeekValue = task.dueDate?.dayOfWeek().value();

  const dayOfWeekTranslated = dayOfWeekValue
    ? EnumDayOfWeek.getByKey(dayOfWeekValue).translation
    : "";

  return $t(Key.fromString(basis.translationKey), {
    day: dayOfWeekTranslated,
    repeatParameter: task.taskRepeatParameter,
    ordinalNumber: appendOrdinalSuffix(task.taskRepeatParameter),
  }).toString();
}

export function useTasksList(
  dueDateRange: Ref<LocalDate[]> = ref<LocalDate[]>([])
) {
  const { $m } = useI18n();
  const users = makeRef<readonly User[]>([]);
  const nextDeliveryAreaDespatchDateLookup = makeRef(
    new GuidMap<LocalDate | null>()
  );
  const outletContactsLookup = makeRef(new GuidMap<Contact>());
  const recentActivityLogEntriesLookup = makeRef(
    new GuidMap<ActivityLogEntry>()
  );
  const cache = apiCache.buildCacheSet({
    taskTypes: TaskTypesCache,
    taskQueues: TaskQueuesCache,
    salesCodes: SalesCodesCache,
    salesAreas: SalesAreasCache,
    deliveryAreas: DeliveryAreasCache,
    groups: GroupsCache,
  });

  // create unassigned user placeholder
  const placeholderNoneUser = new User();
  placeholderNoneUser.id = PLACEHOLDER_USER_ID;
  placeholderNoneUser.name = $m.tasks_no_user_assigned;
  const usersFilterOptions = computed(() => {
    // We query all assigned users so we can determine if we need to add support
    // and system users to the filter options.
    const allAssignedUserIdsInTasks = new GuidSet(
      distinctGuids(
        openTaskRows.value
          .map((tr) => tr.task.assignedToUserId)
          .concat(closedTasks.value.map((tr) => tr.task.assignedToUserId))
      )
    );

    const usersAssignedUsersInTasks = users.value.filter((option) =>
      allAssignedUserIdsInTasks.has(option.id)
    );

    const { items: filterUsers } = useArrayWithPlaceholderItem(
      makeDeepRef(usersAssignedUsersInTasks),
      placeholderNoneUser,
      (user) => user.id
    );

    return filterUsers.value;
  });

  const selectedTaskTypeIds = ref<Guid[]>([]);
  const selectedTaskQueueIds = ref<Guid[]>([]);
  const selectedUserIds = ref<Guid[]>([]);

  const openTaskRows = makeRef<readonly TaskWithAssociations[]>([]);
  const cancelledOrCompletedTasks = makeRef<readonly TaskWithAssociations[]>(
    []
  );

  // Is our closed tasks card open?
  const closedTasksVisible = ref(false);

  // The most common use of 'now', for ease of use
  let dateTimeNow = tenantService.nowDateTime;

  const refreshOpenTasks = withLoading(async () => {
    dateTimeNow = tenantService.nowDateTime;

    const taskFilter = new BmTaskFilter();
    taskFilter.limitStatuses = [EnumTaskStatus.open];

    if (dueDateRange.value.length) {
      taskFilter.fromDueDate = dueDateRange.value[0];
      taskFilter.toDueDate = dueDateRange.value[1] ?? dueDateRange.value[0];
    }

    const openTasks = (
      await bmTaskService.getBmTasksByFilter(api.tenantId, taskFilter)
    ).bmTasks;

    openTaskRows.value = await joinTasksWithAssociations(openTasks);

    return openTasks;
  });

  const refreshClosedTasks = async () => {
    dateTimeNow = tenantService.nowDateTime;

    const closedTasksFilter = new BmTaskFilter();

    closedTasksFilter.limitStatuses = [
      EnumTaskStatus.cancelled,
      EnumTaskStatus.complete,
    ];

    closedTasksFilter.maximumResults = 1000;

    const closedTasks = (
      await bmTaskService.getBmTasksByFilter(api.tenantId, closedTasksFilter)
    ).bmTasks;

    cancelledOrCompletedTasks.value =
      await joinTasksWithAssociations(closedTasks);
  };

  const refreshDependencies = async () => {
    await cache.update();

    const outletIds = distinctGuids(
      openTaskRows.value
        .map((x) => x.associatedOutlet?.id)
        .concat(
          todoTasks.value.map((x) => x.associatedOutlet?.id),
          closedTasks.value.map((x) => x.associatedOutlet?.id)
        )
    );

    const leadIds = distinctGuids(
      openTaskRows.value
        .map((x) => x.associatedLead?.id)
        .concat(
          todoTasks.value.map((x) => x.associatedLead?.id),
          closedTasks.value.map((x) => x.associatedLead?.id)
        )
    );

    const [
      allUsers,
      despatchDatesResult,
      outletContacts,
      outletActivityLogEntries,
      leadActivityLogEntries,
    ] = await Promise.all([
      userService.getAllUsers(api.tenantGroupId),
      deliveryAreaService
        .calculateDeliveryAreasDistributionDates(
          api.tenantId,
          cache.deliveryAreas.all.map((x) => x.id)
        )
        .then(
          (result) => new GuidMap(result.map((r) => [r.id, r.dispatchDate]))
        ),
      outletService.getPrimaryContactsForOutlets(api.tenantId, outletIds),
      activityLogService.getMostRecentActivityLogEntries(
        api.tenantId,
        EnumActivityLogType.communication,
        EnumBmEntityType.outlet,
        outletIds
      ),
      activityLogService.getMostRecentActivityLogEntries(
        api.tenantId,
        EnumActivityLogType.communication,
        EnumBmEntityType.lead,
        leadIds
      ),
    ]);

    recentActivityLogEntriesLookup.value = toGuidMap(
      [...outletActivityLogEntries, ...leadActivityLogEntries].filter(
        (entry) => !!entry.activityOnEntityId
      ),
      (entry) => entry.activityOnEntityId!
    );

    nextDeliveryAreaDespatchDateLookup.value = despatchDatesResult;
    outletContactsLookup.value = toGuidMap(outletContacts, (x) => x.outletId);
    users.value = allUsers;
  };

  const cancelTask = withLoading(async (task: BmTask) => {
    await bmTaskService.cancelBmTask(api.tenantId, task.id);
    await refreshTasksAndDependencies();
  });

  const completeTask = withLoading(async (task: BmTask) => {
    await bmTaskService.completeBmTask(api.tenantId, task.id);
    await refreshTasksAndDependencies();
  });

  const refreshTasksAndDependencies = async (refreshTaskStore = true) => {
    if (refreshTaskStore) {
      // Do not await this refresh call - it doesn't affect any data on the
      // tasks page so does not need to potentially block the UI with a loading
      // dialog
      tasksStore.refresh().then();
    }

    await wrapLoading(async () => {
      const promises: Promise<any>[] = [refreshOpenTasks()];

      // Only attempt to load closed tasks if they have been loaded before
      // by the header click.
      if (closedTasksVisible.value) {
        promises.push(refreshClosedTasks());
      }

      await Promise.all(promises);
      await refreshDependencies();
    });
  };

  const selectedOpenTasks = computed(() =>
    openTaskRows.value.filter((row) =>
      isTaskSelected(
        row.task,
        selectedTaskTypeIds.value,
        selectedTaskQueueIds.value,
        selectedUserIds.value
      )
    )
  );

  // All open tasks that are not overdue, but due today
  const todoTasks = computed(() =>
    selectedOpenTasks.value.filter(
      (row) =>
        !isTaskOverdue(row.task, dateTimeNow) &&
        isTaskInToDoSection(row.task, dateTimeNow)
    )
  );

  // All open tasks that are not overdue, and also not due today
  const upcomingTasks = computed(() =>
    selectedOpenTasks.value.filter(
      (row) =>
        !isTaskOverdue(row.task, dateTimeNow) &&
        !isTaskInToDoSection(row.task, dateTimeNow)
    )
  );

  const overdueTasks = computed(() =>
    selectedOpenTasks.value.filter((row) =>
      isTaskOverdue(row.task, dateTimeNow)
    )
  );

  const closedTasks = computed(() =>
    cancelledOrCompletedTasks.value.filter((row) =>
      isTaskSelected(
        row.task,
        selectedTaskTypeIds.value,
        selectedTaskQueueIds.value,
        selectedUserIds.value
      )
    )
  );

  watch(
    closedTasksVisible,
    withLoading(
      wrapPromiseWithErrorHandling(async () => {
        if (closedTasksVisible.value) {
          await refreshTasksAndDependencies();
        } else {
          cancelledOrCompletedTasks.value = [];
        }
      })
    )
  );

  return {
    users,
    cache,
    nextDeliveryAreaDespatchDateLookup,
    outletContactsLookup,
    recentActivityLogEntriesLookup,

    usersMap: computed(
      () => new GuidMap(users.value.map((user) => [user.id, user]))
    ),

    // Task lists
    openTaskRows,
    todoTasks,
    upcomingTasks,
    overdueTasks,
    closedTasks,

    closedTasksVisible,

    // Filter items
    usersFilterOptions,
    selectedTaskTypeIds,
    selectedTaskQueueIds,
    selectedUserIds,

    cancelTask,
    completeTask,
    refreshTasksAndDependencies,
  };
}
